import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import GridAlphabetIcon from '../components/GridAlphabet/GridAlphabetIcons';
import FarsiKeyboard from '../components/FarsiKeyboard';
import AlphabetTracing from '../components/AlphabetTracing';
import { 
  FaPlay, 
  FaCheck, 
  FaArrowLeft, 
  FaVolumeUp, 
  FaStar,
  FaBookOpen,
  FaClock,
  FaGraduationCap,
  FaLightbulb,
  FaGamepad,
  FaMicrophone,
  FaPencilAlt,
  FaDrawPolygon
} from 'react-icons/fa';
import './GridAlphabetLesson.css';
import '../components/GridAlphabet/GridAlphabetIcons.css';
import { playAudio } from '../utils/audioUtils';

interface VocabularyItem {
  id: string;
  persian: string;
  english: string;
  transliteration: string;
  icon: string;
  iconType: 'svg' | 'base64';
  audioUrl?: string;
  pronunciation?: string;
  example?: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category?: string;
  tags?: string[];
}

interface GridAlphabetLesson {
  id: string;
  letter: string;
  letterName: string;
  letterNameEnglish: string;
  letterNamePersian: string;
  title: string;
  description: string;
  level: string;
  vocabularyItems: VocabularyItem[];
  order: number;
  estimatedDuration: number;
  difficulty: string;
  topics: string[];
  styleTags: string[];
  learningGoals: string[];
  activities: {
    matching: {
      enabled: boolean;
      pairs: Array<{
        persian: string;
        english: string;
        transliteration: string;
      }>;
    };
    multipleChoice: {
      enabled: boolean;
      questions: Array<{
        question: string;
        options: string[];
        correctAnswer: string;
        explanation: string;
      }>;
    };
    pronunciation: {
      enabled: boolean;
      words: string[];
    };
    writing: {
      enabled: boolean;
      practiceWords: string[];
    };
  };
  completionCriteria: {
    vocabularyMastered: number;
    activitiesCompleted: number;
    timeSpent: number;
  };
}

const GridAlphabetLesson: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [currentActivity, setCurrentActivity] = useState<'vocabulary' | 'matching' | 'multiple-choice' | 'pronunciation' | 'writing' | 'tracing'>('vocabulary');
  const [completedActivities, setCompletedActivities] = useState<string[]>([]);
  const [matchingPairs, setMatchingPairs] = useState<{ persian: string; english: string; transliteration: string }[]>([]);
  const [selectedPersian, setSelectedPersian] = useState<string | null>(null);
  const [selectedEnglish, setSelectedEnglish] = useState<string | null>(null);
  const [matchedPairs, setMatchedPairs] = useState<{ [key: string]: string }>({});
  const [multipleChoiceAnswers, setMultipleChoiceAnswers] = useState<{ [key: number]: string }>({});
  const [pronunciationAttempts, setPronunciationAttempts] = useState<{ [key: string]: boolean }>({});
  const [writingText, setWritingText] = useState('');

  // Fetch the grid alphabet lesson
  const { data: lesson, isLoading, error } = useQuery({
    queryKey: ['grid-alphabet-lesson', lessonId],
    queryFn: async () => {
      console.log('🔍 Fetching lesson with ID:', lessonId);
      console.log('🔍 API Base URL:', API_BASE_URL);
      
      // Letter to ID mapping for Persian alphabet lessons
      const letterToIdMap: { [key: string]: string } = {
        'آ': 'alphabet-1-alef',
        'ب': 'alphabet-2-be',
        'پ': 'alphabet-3-pe',
        'ت': 'alphabet-4-te',
        'ث': 'alphabet-5-se',
        'ج': 'alphabet-6-jim',
        'چ': 'alphabet-7-che',
        'ح': 'alphabet-8-he',
        'خ': 'alphabet-9-khe',
        'د': 'alphabet-10-dal',
        'ذ': 'alphabet-11-zal',
        'ر': 'alphabet-12-re',
        'ز': 'alphabet-13-ze',
        'ژ': 'alphabet-14-zhe',
        'س': 'alphabet-15-sin',
        'ش': 'alphabet-16-shin',
        'ص': 'alphabet-17-sad',
        'ض': 'alphabet-18-zad',
        'ط': 'alphabet-19-ta',
        'ظ': 'alphabet-20-za',
        'ع': 'alphabet-21-ayn',
        'غ': 'alphabet-22-ghayn',
        'ف': 'alphabet-23-fe',
        'ق': 'alphabet-24-qaf',
        'ک': 'alphabet-25-kaf',
        'گ': 'alphabet-26-gaf',
        'ل': 'alphabet-27-lam',
        'م': 'alphabet-28-mim',
        'ن': 'alphabet-29-nun',
        'و': 'alphabet-30-vav',
        'ه': 'alphabet-31-he',
        'ی': 'alphabet-32-ye'
      };
      
      // Check if lessonId is a Persian letter and map it to the correct ID
      let actualLessonId = lessonId;
      if (letterToIdMap[lessonId]) {
        actualLessonId = letterToIdMap[lessonId];
        console.log('🔍 Mapped letter', lessonId, 'to ID:', actualLessonId);
      }
      
      // First, try to fetch the lesson directly from the GridAlphabetLesson collection
      try {
        const directLessonUrl = `${API_BASE_URL}/api/grid-alphabet/${actualLessonId}`;
        console.log('🔍 Trying direct lesson fetch:', directLessonUrl);
        const directResponse = await axios.get(directLessonUrl);
        if (directResponse.data.success && directResponse.data.data) {
          console.log('✅ Found lesson directly:', directResponse.data.data);
          return directResponse.data.data;
        }
      } catch (error) {
        console.log('❌ Direct lesson fetch failed:', error instanceof Error ? error.message : String(error));
      }
      
      // If direct fetch fails, try to find the lesson in pathways
      const pathwaysToCheck = [
        { id: 'alphabet-learning', name: 'Alphabet Learning' },
        { id: '6875e2c70e9af9bca4a60a0c', name: 'Absolute Beginner' },
        { id: 'alphabet-learning-pathway', name: 'Alphabet Learning Pathway' }
      ];
      
      let lessonFromPathway = null;
      let pathwayData = null;
      
      for (const pathway of pathwaysToCheck) {
        try {
          console.log(`🔍 Trying pathway: ${pathway.name} (${pathway.id})`);
          const pathwayUrl = `${API_BASE_URL}/api/pathways/${pathway.id}`;
          console.log('🔍 Fetching from URL:', pathwayUrl);
          
          const pathwayResponse = await axios.get(pathwayUrl);
          pathwayData = pathwayResponse.data.data || pathwayResponse.data;
          console.log('✅ Pathway data received:', pathwayData);
          
          if (pathwayData.lessons) {
            console.log('📚 Available lesson IDs:', pathwayData.lessons.map((l: any) => ({ id: l.id, title: l.title, letter: l.content?.letter })));
            
            // Try to find the lesson by ID
            lessonFromPathway = pathwayData.lessons.find((l: any) => l.id === actualLessonId);
            
            // If not found by ID, try to find by letter or title
            if (!lessonFromPathway) {
              lessonFromPathway = pathwayData.lessons.find((l: any) => 
                l.content?.letter === actualLessonId ||
                l.title.includes(actualLessonId) ||
                l.id === actualLessonId
              );
            }
            
            if (lessonFromPathway) {
              console.log('✅ Lesson found in pathway:', pathway.name);
              break;
            }
          }
        } catch (error) {
          console.log(`❌ Pathway ${pathway.name} not found or error:`, error instanceof Error ? error.message : String(error));
          continue;
        }
      }
      
      // If no lesson found, try to fetch from GridAlphabetLesson by letter
      if (!lessonFromPathway) {
        try {
          console.log('🔍 Trying to fetch lesson by letter:', actualLessonId);
          const letterLessonUrl = `${API_BASE_URL}/api/grid-alphabet/letter/${actualLessonId}`;
          const letterResponse = await axios.get(letterLessonUrl);
          if (letterResponse.data.success && letterResponse.data.data) {
            console.log('✅ Found lesson by letter:', letterResponse.data.data);
            return letterResponse.data.data;
          }
        } catch (error) {
          console.log('❌ Letter-based lesson fetch failed:', error instanceof Error ? error.message : String(error));
        }
      }
      
      // If still no lesson found, try to search for lessons that might contain this letter
      if (!lessonFromPathway) {
        try {
          console.log('🔍 Trying to search for lessons containing letter:', actualLessonId);
          const searchUrl = `${API_BASE_URL}/api/grid-alphabet/search?q=${encodeURIComponent(actualLessonId)}`;
          const searchResponse = await axios.get(searchUrl);
          if (searchResponse.data.success && searchResponse.data.data && searchResponse.data.data.length > 0) {
            console.log('✅ Found lesson by search:', searchResponse.data.data[0]);
            return searchResponse.data.data[0];
          }
        } catch (error) {
          console.log('❌ Search-based lesson fetch failed:', error instanceof Error ? error.message : String(error));
        }
      }
      
      // If still no lesson found, try to get all lessons and find one that matches
      if (!lessonFromPathway) {
        try {
          console.log('🔍 Trying to get all lessons and find match for:', actualLessonId);
          const allLessonsUrl = `${API_BASE_URL}/api/grid-alphabet`;
          const allLessonsResponse = await axios.get(allLessonsUrl);
          if (allLessonsResponse.data.success && allLessonsResponse.data.data) {
            const allLessons = allLessonsResponse.data.data;
            const matchingLesson = allLessons.find((l: any) => 
              l.letter === actualLessonId || 
              l.title.includes(actualLessonId) || 
              l.id === actualLessonId
            );
            if (matchingLesson) {
              console.log('✅ Found lesson in all lessons:', matchingLesson);
              return matchingLesson;
            }
          }
        } catch (error) {
          console.log('❌ All lessons fetch failed:', error instanceof Error ? error.message : String(error));
        }
      }
      
      // If still no lesson found, create a fallback lesson
      if (!lessonFromPathway) {
        console.log('🔄 Creating fallback lesson for:', actualLessonId);
        lessonFromPathway = {
          id: actualLessonId,
          title: `📘 Lesson: Letter ${actualLessonId}`,
          description: `Learn how to recognize, read, and say words that use the letter ${actualLessonId}.`,
          content: {
            letter: actualLessonId,
            letterName: actualLessonId,
            letterNameEnglish: actualLessonId,
            letterNamePersian: actualLessonId,
            level: 'Beginner',
            difficulty: 'easy',
            topics: ['Alphabet & Writing'],
            styleTags: ['Visual Learning', 'Interactive'],
            learningGoals: [`Master the letter ${actualLessonId}`],
            vocabularyItems: [
              {
                id: '1',
                persian: actualLessonId,
                pronunciation: actualLessonId,
                english: `Letter ${actualLessonId}`,
                transliteration: actualLessonId,
                icon: actualLessonId,
                iconType: 'svg' as const,
                difficulty: 'easy' as const
              }
            ],
            activities: {
              matching: { enabled: false, pairs: [] },
              multipleChoice: { enabled: false, questions: [] },
              pronunciation: { enabled: false, words: [] },
              writing: { enabled: false, practiceWords: [] }
            },
            completionCriteria: { vocabularyMastered: 0, activitiesCompleted: 0, timeSpent: 15 },
            estimatedDuration: 15
          },
          order: 1,
          estimatedDuration: 15,
          difficulty: 'easy',
          topics: ['Alphabet & Writing'],
          styleTags: ['Visual Learning', 'Interactive'],
          learningGoals: [`Master the letter ${actualLessonId}`],
          activities: {
            matching: { enabled: false, pairs: [] },
            multipleChoice: { enabled: false, questions: [] },
            pronunciation: { enabled: false, words: [] },
            writing: { enabled: false, practiceWords: [] }
          },
          completionCriteria: { vocabularyMastered: 0, activitiesCompleted: 0, timeSpent: 15 }
        };
      }
      
      console.log('✅ Final lesson data:', lessonFromPathway);
      
      // Transform the pathway lesson data to match our interface
      return {
        id: lessonFromPathway.id || actualLessonId,
        letter: lessonFromPathway.content?.letter || lessonFromPathway.letter || actualLessonId,
        letterName: lessonFromPathway.content?.letterName || lessonFromPathway.letterName || actualLessonId,
        letterNameEnglish: lessonFromPathway.content?.letterNameEnglish || lessonFromPathway.letterNameEnglish || actualLessonId,
        letterNamePersian: lessonFromPathway.content?.letterNamePersian || lessonFromPathway.letterNamePersian || actualLessonId,
        title: lessonFromPathway.title,
        description: lessonFromPathway.description,
        level: lessonFromPathway.content?.level || lessonFromPathway.level || 'beginner',
        vocabularyItems: lessonFromPathway.content?.vocabularyItems || lessonFromPathway.vocabularyItems || [],
        order: lessonFromPathway.content?.order || lessonFromPathway.order || 1,
        estimatedDuration: lessonFromPathway.content?.estimatedDuration || lessonFromPathway.estimatedDuration || 15,
        difficulty: lessonFromPathway.content?.difficulty || lessonFromPathway.difficulty || 'easy',
        topics: lessonFromPathway.content?.topics || lessonFromPathway.topics || [],
        styleTags: lessonFromPathway.content?.styleTags || lessonFromPathway.styleTags || [],
        learningGoals: lessonFromPathway.content?.learningGoals || lessonFromPathway.learningGoals || [],
        activities: lessonFromPathway.content?.activities || lessonFromPathway.activities || {
          matching: { enabled: false, pairs: [] },
          multipleChoice: { enabled: false, questions: [] },
          pronunciation: { enabled: false, words: [] },
          writing: { enabled: false, practiceWords: [] }
        },
        completionCriteria: lessonFromPathway.content?.completionCriteria || lessonFromPathway.completionCriteria || {
          vocabularyMastered: 0,
          activitiesCompleted: 0,
          timeSpent: 0
        }
      } as GridAlphabetLesson;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Debug: Log lesson data when it changes
  useEffect(() => {
    if (lesson) {
      console.log('Lesson loaded:', lesson);
    }
  }, [lesson]);

  // Load completed activities from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`completedActivities_${lessonId}`);
    if (saved) {
      const savedActivities = JSON.parse(saved);
      setCompletedActivities(savedActivities);
      
      // Check if lesson is already completed
      checkLessonCompletion(savedActivities);
    }
  }, [lessonId, lesson]);

  // Initialize matching pairs when lesson loads
  useEffect(() => {
    if (lesson?.activities?.matching?.pairs) {
      setMatchingPairs([...lesson.activities.matching.pairs].sort(() => Math.random() - 0.5));
    }
  }, [lesson]);

  // Reset matching state when switching activities
  useEffect(() => {
    if (currentActivity !== 'matching') {
      setSelectedPersian(null);
      setSelectedEnglish(null);
      setMatchedPairs({});
    }
  }, [currentActivity]);

  const handleActivityComplete = (activityType: string) => {
    if (!completedActivities.includes(activityType)) {
      const newCompleted = [...completedActivities, activityType];
      setCompletedActivities(newCompleted);
      localStorage.setItem(`completedActivities_${lessonId}`, JSON.stringify(newCompleted));
      
      // Check if lesson is fully completed
      checkLessonCompletion(newCompleted);
    }
  };

  const checkLessonCompletion = (completedActivitiesList: string[]) => {
    if (!lesson) return;
    
    console.log('🔍 Checking lesson completion:', {
      lessonId,
      lessonTitle: lesson.title,
      lessonDataId: lesson.id,
      completedActivitiesList
    });
    
    // Define required activities for completion
    const requiredActivities = ['vocabulary'];
    
    // Add other activities if they are enabled
    if (lesson.activities.matching.enabled) {
      requiredActivities.push('matching');
    }
    if (lesson.activities.multipleChoice.enabled) {
      requiredActivities.push('multiple-choice');
    }
    if (lesson.activities.pronunciation.enabled) {
      requiredActivities.push('pronunciation');
    }
    if (lesson.activities.writing.enabled) {
      requiredActivities.push('writing');
    }
    
    // Check if all required activities are completed
    const allCompleted = requiredActivities.every(activity => 
      completedActivitiesList.includes(activity)
    );
    
    console.log('🔍 Lesson completion check:', {
      requiredActivities,
      allCompleted
    });
    
    if (allCompleted) {
      // Mark lesson as completed in the pathway progress
      // Use lesson.id from the lesson data instead of lessonId from URL
      const lessonIdToSave = lesson.id || lessonId;
      const savedCompletedLessons = localStorage.getItem('completedAlphabetLessons');
      const completedLessons = savedCompletedLessons ? JSON.parse(savedCompletedLessons) : [];
      
      console.log('🔍 Before saving completion:', {
        lessonIdFromUrl: lessonId,
        lessonIdFromData: lesson.id,
        lessonIdToSave,
        completedLessons
      });
      
      // Validate that the lesson ID is valid before saving
      if (lessonIdToSave && !completedLessons.includes(lessonIdToSave)) {
        completedLessons.push(lessonIdToSave);
        localStorage.setItem('completedAlphabetLessons', JSON.stringify(completedLessons));
        console.log(`✅ Lesson ${lessonIdToSave} marked as completed!`);
        console.log('🔍 After saving completion:', completedLessons);
      } else if (completedLessons.includes(lessonIdToSave)) {
        console.log(`⚠️ Lesson ${lessonIdToSave} already marked as completed`);
      } else {
        console.log(`❌ Invalid lesson ID: ${lessonIdToSave}`);
      }
    }
  };

  const handleMatchingSelection = (type: 'persian' | 'english', value: string) => {
    if (type === 'persian') {
      if (selectedPersian === value) {
        // Deselect if already selected
        setSelectedPersian(null);
      } else {
        setSelectedPersian(value);
        // If English is already selected, try to make a match
        if (selectedEnglish) {
          handleMatchAttempt(value, selectedEnglish);
        }
      }
    } else {
      if (selectedEnglish === value) {
        // Deselect if already selected
        setSelectedEnglish(null);
      } else {
        setSelectedEnglish(value);
        // If Persian is already selected, try to make a match
        if (selectedPersian) {
          handleMatchAttempt(selectedPersian, value);
        }
      }
    }
  };

  const handleMatchAttempt = (persian: string, english: string) => {
    // Check if this is a correct match
    const isCorrectMatch = matchingPairs.some(pair => 
      pair.persian === persian && pair.english === english
    );

    if (isCorrectMatch) {
      // Add to matched pairs
      setMatchedPairs(prev => ({
        ...prev,
        [persian]: english
      }));
      // Clear selections
      setSelectedPersian(null);
      setSelectedEnglish(null);
    } else {
      // Incorrect match - clear selections after a brief delay
      setTimeout(() => {
        setSelectedPersian(null);
        setSelectedEnglish(null);
      }, 1000);
    }
  };

  const checkMatchingCompletion = () => {
    if (!lesson?.activities?.matching?.pairs) return false;
    return Object.keys(matchedPairs).length === lesson.activities.matching.pairs.length;
  };

  const handleMultipleChoiceAnswer = (questionIndex: number, answer: string) => {
    setMultipleChoiceAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  const checkMultipleChoiceCompletion = () => {
    if (!lesson?.activities?.multipleChoice?.questions) return false;
    
    const correctAnswers = lesson.activities.multipleChoice.questions.filter((question, index) => 
      multipleChoiceAnswers[index] === question.correctAnswer
    );
    
    return correctAnswers.length === lesson.activities.multipleChoice.questions.length;
  };

  const handlePronunciationAttempt = (word: string) => {
    setPronunciationAttempts(prev => ({
      ...prev,
      [word]: true
    }));
  };

  const checkPronunciationCompletion = () => {
    if (!lesson?.activities?.pronunciation?.words) return false;
    
    const attemptedWords = lesson.activities.pronunciation.words.filter(word => 
      pronunciationAttempts[word]
    );
    
    return attemptedWords.length === lesson.activities.pronunciation.words.length;
  };

  const getProgressPercentage = () => {
    if (!lesson) return 0;
    
    // Calculate the actual number of enabled activities
    let totalActivities = 1; // Always count vocabulary as 1 activity
    
    if (lesson.activities.matching.enabled) totalActivities++;
    if (lesson.activities.multipleChoice.enabled) totalActivities++;
    if (lesson.activities.pronunciation.enabled) totalActivities++;
    if (lesson.activities.writing.enabled) totalActivities++;
    
    const completedCount = completedActivities.length;
    return totalActivities > 0 ? Math.min((completedCount / totalActivities) * 100, 100) : 0;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700">Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (error || !lesson) {
    console.error('Error loading lesson:', error);
    console.log('Lesson data:', lesson);
    console.log('Lesson ID being requested:', lessonId);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📚</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Lesson Not Found</h2>
          <p className="text-gray-700 mb-4">
            The lesson could not be loaded. Lesson ID: {lessonId}
          </p>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-left max-w-md">
              <p className="text-red-800 text-sm">
                <strong>Error:</strong> {error instanceof Error ? error.message : 'Unknown error'}
              </p>
              <p className="text-red-600 text-xs mt-2">
                This might be because the lesson data is not available or the server is not responding.
              </p>
            </div>
          )}
          <div className="space-y-2">
            <button
              onClick={() => navigate('/alphabet-learning')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition mr-2"
            >
              Back to Alphabet Learning
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 grid-alphabet-lesson">
      <div className="container mx-auto px-4 py-8">
        {/* Debug Panel - Remove this in production */}
        {process.env.NODE_ENV === 'development' && lesson && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Debug Info</h3>
            <div className="text-sm text-yellow-700">
              <p><strong>Lesson ID:</strong> {lessonId}</p>
              <p><strong>Title:</strong> {lesson.title}</p>
              <p><strong>Letter:</strong> {lesson.letter}</p>
              <p><strong>Vocabulary Items:</strong> {lesson.vocabularyItems?.length || 0}</p>
              <p><strong>Activities:</strong> {JSON.stringify(lesson.activities, null, 2)}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/alphabet-learning')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4 transition"
          >
            <FaArrowLeft className="mr-2" />
            Back to Alphabet Learning
          </button>
          
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-6">
                  <span className="text-white text-4xl font-bold">
                    {lesson.letter}
                  </span>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{lesson.title}</h1>
                  <p className="text-gray-700">{lesson.description}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{Math.round(getProgressPercentage())}%</div>
                <div className="text-sm text-gray-500">Complete</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
              <div 
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${getProgressPercentage()}%` }}
              ></div>
            </div>

            {/* Lesson Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center text-gray-600">
                <FaClock className="mr-2" />
                <span>{lesson.estimatedDuration} min</span>
              </div>
              <div className="flex items-center text-gray-600">
                <FaBookOpen className="mr-2" />
                <span>{lesson.vocabularyItems.length} words</span>
              </div>
              <div className="flex items-center text-gray-600">
                <FaGraduationCap className="mr-2" />
                <span>{lesson.level}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <FaStar className="mr-2" />
                <span>{lesson.difficulty}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Learning Goals */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <FaLightbulb className="mr-2 text-yellow-500" />
            Learning Goals
          </h2>
          <ul className="space-y-2">
            {lesson.learningGoals.map((goal, index) => (
              <li key={index} className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span className="text-gray-700">{goal}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Activity Navigation */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Activities</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { id: 'vocabulary', name: 'Vocabulary', icon: FaBookOpen, color: 'blue' },
              { id: 'matching', name: 'Matching', icon: FaGamepad, color: 'green' },
              { id: 'multiple-choice', name: 'Quiz', icon: FaStar, color: 'purple' },
              { id: 'pronunciation', name: 'Pronunciation', icon: FaMicrophone, color: 'orange' },
              { id: 'writing', name: 'Writing', icon: FaPencilAlt, color: 'red' },
              { id: 'tracing', name: 'Tracing', icon: FaDrawPolygon, color: 'indigo' }
            ].map((activity) => (
              <button
                key={activity.id}
                onClick={() => setCurrentActivity(activity.id as any)}
                className={`p-4 rounded-lg text-center transition ${
                  currentActivity === activity.id
                    ? `bg-${activity.color}-100 border-2 border-${activity.color}-500`
                    : 'bg-gray-50 hover:bg-gray-100'
                } ${completedActivities.includes(activity.id) ? 'ring-2 ring-green-500' : ''}`}
              >
                <activity.icon className={`text-2xl mx-auto mb-2 ${
                  currentActivity === activity.id ? `text-${activity.color}-600` : 'text-gray-600'
                }`} />
                <div className="text-sm font-medium">{activity.name}</div>
                {completedActivities.includes(activity.id) && (
                  <FaCheck className="text-green-500 text-sm mx-auto mt-1" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Activity Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {currentActivity === 'vocabulary' && (
            <div className="bg-white">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Vocabulary</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {lesson.vocabularyItems.map((item) => (
                  <div key={item.id} className="vocabulary-card bg-white">
                    <div className="icon-container">
                      <GridAlphabetIcon 
                        icon={item.icon} 
                        size={32} 
                        data-icon={item.icon}
                      />
                    </div>
                    <div className="content">
                      <div className="persian">{item.persian}</div>
                      <div className="transliteration">{item.transliteration}</div>
                      <div className="english">{item.english}</div>
                      
                      {item.audioUrl && (
                        <button
                          onClick={() => playAudio(item.audioUrl!, item.persian)}
                          className="mt-2 p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <FaVolumeUp className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    {item.example && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm text-gray-700 mb-1">Example:</div>
                        <div className="text-gray-900">{item.example}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 text-center">
                <button
                  onClick={() => handleActivityComplete('vocabulary')}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Complete Vocabulary
                </button>
              </div>
            </div>
          )}

          {currentActivity === 'matching' && lesson.activities.matching.enabled && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Match the Words</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Persian Words</h3>
                  <div className="space-y-3">
                    {matchingPairs.map((pair) => (
                      <div
                        key={pair.persian}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                          matchedPairs[pair.persian]
                            ? 'border-green-500 bg-green-50 opacity-60'
                            : selectedPersian === pair.persian
                            ? 'border-blue-500 bg-gray-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                        onClick={() => !matchedPairs[pair.persian] && handleMatchingSelection('persian', pair.persian)}
                      >
                        <div className="text-xl font-bold text-gray-900">{pair.persian}</div>
                        <div className="text-sm text-gray-600">{pair.transliteration}</div>
                        {matchedPairs[pair.persian] && (
                          <div className="text-xs text-green-600 mt-1">✓ Matched</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">English Meanings</h3>
                  <div className="space-y-3">
                    {matchingPairs.map((pair) => (
                      <div
                        key={pair.english}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                          Object.values(matchedPairs).includes(pair.english)
                            ? 'border-green-500 bg-green-50 opacity-60'
                            : selectedEnglish === pair.english
                            ? 'border-blue-500 bg-gray-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                        onClick={() => !Object.values(matchedPairs).includes(pair.english) && handleMatchingSelection('english', pair.english)}
                      >
                        <div className="text-lg font-semibold text-gray-900">{pair.english}</div>
                        {Object.values(matchedPairs).includes(pair.english) && (
                          <div className="text-xs text-green-600 mt-1">✓ Matched</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Progress indicator */}
              <div className="mt-6 text-center">
                <div className="text-sm text-gray-700 mb-2">
                  Matched: {Object.keys(matchedPairs).length} / {matchingPairs.length}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(Object.keys(matchedPairs).length / matchingPairs.length) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {checkMatchingCompletion() && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => handleActivityComplete('matching')}
                    className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    Complete Matching
                  </button>
                </div>
              )}
            </div>
          )}

          {currentActivity === 'multiple-choice' && lesson.activities.multipleChoice.enabled && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Quiz</h2>
              <div className="space-y-8">
                {lesson.activities.multipleChoice.questions.map((question, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{question.question}</h3>
                    <div className="space-y-3">
                      {question.options.map((option) => (
                        <button
                          key={option}
                          onClick={() => handleMultipleChoiceAnswer(index, option)}
                          className={`w-full p-4 rounded-lg text-left transition ${
                            multipleChoiceAnswers[index] === option
                              ? 'bg-blue-100 border-2 border-blue-500'
                              : 'bg-white hover:bg-gray-100 border-2 border-gray-200'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {multipleChoiceAnswers[index] && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <div className="text-sm text-gray-700 mb-1">Explanation:</div>
                        <div className="text-gray-900">{question.explanation}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {checkMultipleChoiceCompletion() && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => handleActivityComplete('multiple-choice')}
                    className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    Complete Quiz
                  </button>
                </div>
              )}
            </div>
          )}

          {currentActivity === 'pronunciation' && lesson.activities.pronunciation.enabled && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Pronunciation Practice</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {lesson.activities.pronunciation.words.map((word) => (
                  <div key={word} className="bg-gray-50 rounded-xl p-6 text-center">
                    <div className="text-3xl font-bold text-gray-900 mb-4">{word}</div>
                    <button
                      onClick={() => handlePronunciationAttempt(word)}
                      className={`px-6 py-3 rounded-lg transition ${
                        pronunciationAttempts[word]
                          ? 'bg-green-600 text-white'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {pronunciationAttempts[word] ? 'Practiced' : 'Practice'}
                    </button>
                  </div>
                ))}
              </div>
              
              {checkPronunciationCompletion() && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => handleActivityComplete('pronunciation')}
                    className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    Complete Pronunciation
                  </button>
                </div>
              )}
            </div>
          )}

          {currentActivity === 'writing' && lesson.activities.writing.enabled && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Writing Practice</h2>
              <div className="mb-6">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FaPencilAlt className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-800">
                        Practice writing Persian words using the Farsi keyboard below. Try writing the suggested words or create your own sentences.
                      </p>
                    </div>
                  </div>
                </div>
                
                <FarsiKeyboard
                  onInputChange={setWritingText}
                  currentText={writingText}
                  placeholder="Practice writing Persian words..."
                />
                
                {lesson.activities.writing.practiceWords.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Suggested words to practice:</h3>
                    <div className="flex flex-wrap gap-3">
                      {lesson.activities.writing.practiceWords.map((word) => (
                        <button
                          key={word}
                          onClick={() => setWritingText(writingText + word)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
                        >
                          {word}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-6 text-center">
                <button
                  onClick={() => handleActivityComplete('writing')}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Complete Writing Practice
                </button>
              </div>
            </div>
          )}

          {currentActivity === 'tracing' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Letter Tracing Practice</h2>
              <div className="mb-6">
                <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FaDrawPolygon className="h-5 w-5 text-indigo-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-indigo-800">
                        Practice writing the letter {lesson.letter} by tracing its shape. Follow the guide lines and complete each stroke in the correct order.
                      </p>
                    </div>
                  </div>
                </div>
                
                <AlphabetTracing
                  letter={lesson.letter}
                  onComplete={(completedLetter, accuracy) => {
                    // Mark tracing as completed
                    if (!completedActivities.includes('tracing')) {
                      setCompletedActivities(prev => [...prev, 'tracing']);
                    }
                  }}
                  onAudioPlay={(audioUrl) => {
                    playAudio(audioUrl);
                  }}
                />
              </div>
              
              <div className="mt-6 text-center">
                <button
                  onClick={() => handleActivityComplete('tracing')}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Complete Tracing Practice
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GridAlphabetLesson; 